import React from 'react';
import { format } from 'date-fns';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AvailablePeriod } from '@/types/recommendation';

interface AvailabilityWindowProps {
  availablePeriods: AvailablePeriod[];
  availabilityStatus: 'free' | 'partial' | 'conflict';
  requestedStart: Date;
  requestedEnd: Date;
  compact?: boolean;
}

export function AvailabilityWindow({
  availablePeriods,
  availabilityStatus,
  requestedStart,
  requestedEnd,
  compact = false,
}: AvailabilityWindowProps) {
  if (compact) {
    if (availabilityStatus === 'free') {
      return (
        <Badge variant="accent" accent="success" className="gap-1 text-xs py-0.5 px-2 h-6">
          <CheckCircle className="h-3.5 w-3.5 shrink-0" />
          Free
        </Badge>
      );
    }
    if (availabilityStatus === 'partial') {
      return (
        <Badge variant="accent" accent="warning" className="gap-1 text-xs py-0.5 px-2 h-6">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          Partial
        </Badge>
      );
    }
    return (
      <Badge variant="accent" accent="destructive" className="gap-1 text-xs py-0.5 px-2 h-6">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        Conflict
      </Badge>
    );
  }

  // Full mode
  if (availabilityStatus === 'free') {
    return (
      <div className="flex items-center gap-1.5 text-sm accent-success">
        <CheckCircle className="h-4 w-4 text-[var(--accent-bg-solid)] shrink-0" />
        <span>
          Free {format(requestedStart, 'dd MMM')} – {format(requestedEnd, 'dd MMM')}
        </span>
      </div>
    );
  }

  if (availabilityStatus === 'partial') {
    const firstPeriod = availablePeriods[0];
    return (
      <div className="flex items-center gap-1.5 text-sm accent-warning">
        <Clock className="h-4 w-4 text-[var(--accent-bg-solid)] shrink-0" />
        <span>
          Partially free
          {firstPeriod
            ? ` — ${format(new Date(firstPeriod.start), 'dd MMM')} – ${format(new Date(firstPeriod.end), 'dd MMM')}`
            : ''}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 text-sm accent-destructive">
      <AlertTriangle className="h-4 w-4 text-[var(--accent-bg-solid)] shrink-0" />
      <span>Unavailable for this period</span>
    </div>
  );
}
